// Office "Aged debtors" page (admin/office; Session AK Item 4). Outstanding customer invoices
// (authorised / overdue) grouped by how far past due they are: Current, 1 to 30, 31 to 60, 61 to 90,
// 90+ days. The headline is the total outstanding across every overdue band (not Current). The data
// is the InvoiceCache mirror of Xero; "Sync from Xero" refreshes it on demand (the daily cron also
// does). Read-only -- the office acts on an invoice in Xero (each row links straight to it).
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { ApiError, api } from "../../lib/api";
import type { AgedDebtorReport, AgedBandGroup, AgedDebtorRow } from "../../lib/api";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { EmptyState } from "../../components/EmptyState";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";
import { ListToolbar } from "../../components/ListToolbar";
import { useListSearch, filterRows, type SearchAccessors } from "../../lib/useListSearch";
import { useSortableColumn, sortRows, type SortAccessors, type SortableColumnApi } from "../../lib/useSortableColumn";
import { SortableHeader } from "../../components/SortableHeader";
import { DraftChaserModal } from "./InvoiceActionModals";

function gbp(n: number): string {
  return `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function xeroHref(xeroInvoiceId: string): string {
  return `https://go.xero.com/AccountsReceivable/Edit.aspx?InvoiceID=${encodeURIComponent(xeroInvoiceId)}`;
}

const DEBTOR_SEARCH_ACCESSORS: SearchAccessors<AgedDebtorRow> = [(inv) => inv.customerName, (inv) => inv.invoiceNumber];
const DEBTOR_SORT_ACCESSORS: SortAccessors<AgedDebtorRow> = {
  customer: (inv) => inv.customerName,
  invoice: (inv) => inv.invoiceNumber,
  amount: (inv) => inv.amount,
  due: (inv) => inv.dueDate,
  overdue: (inv) => inv.daysOverdue,
};

export function DebtorsPage() {
  // Session SC9B: ?onStop=true narrows the report to the customers on stop (the dashboard tile and the briefing link here). No new page.
  const [searchParams] = useSearchParams();
  const onStopOnly = searchParams.get("onStop") === "true";
  const [report, setReport] = useState<AgedDebtorReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setReport(await api.invoices.agedDebtors(onStopOnly ? { onStop: true } : undefined));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [onStopOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  async function sync() {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const res = await api.billing.syncInvoices();
      setSyncMsg(`Synced ${res.ingested} invoice${res.ingested === 1 ? "" : "s"} from Xero.`);
      await load();
    } catch (err) {
      setSyncMsg(
        err instanceof ApiError && err.status === 503
          ? "Xero is not connected, so the invoices could not be synced."
          : "Could not sync from Xero. Please try again in a moment.",
      );
    } finally {
      setSyncing(false);
    }
  }

  const overdueGroups = (report?.groups ?? []).filter((g) => g.band !== "current");
  const current = (report?.groups ?? []).find((g) => g.band === "current") ?? null;

  // M-10, Session AQT: one search + sort, applied inside each band's own table (BandTable), so
  // a band whose invoices are all filtered out disappears rather than showing an empty table --
  // extending the existing "every g.count === 0" all-clear message to the search case too.
  const { search, setSearch } = useListSearch();
  const sort = useSortableColumn("debtors");

  // Phase 8 (Session ATJ, M-7): the missing manual "Draft chaser" trigger, per overdue invoice.
  const [chaserInvoice, setChaserInvoice] = useState<string | null>(null);
  const [chaserDone, setChaserDone] = useState<string | null>(null);

  return (
    <div>
      <PageHeader
        title="Aged debtors"
        className="mb-4"
        subtitle="Outstanding customer invoices grouped by how far past due they are. The total outstanding is every overdue band, not current."
        actions={
          <Button variant="secondary" size="sm" onClick={sync} disabled={syncing}>
            {syncing ? "Syncing..." : "Sync from Xero"}
          </Button>
        }
      />

      {onStopOnly && report?.onStop && (
        <p role="status" className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-3 text-sm text-ele-strong">
          {report.onStop.state === "not_checked"
            ? "On stop: not checked. The invoice read from Xero is out of date, so no account is cleared. Sync from Xero to check again."
            : `Showing the ${report.onStop.customers} customer${report.onStop.customers === 1 ? "" : "s"} on stop: an invoice more than 30 days past its due date.${report.onStop.unlinkedOverdue > 0 ? ` ${report.onStop.unlinkedOverdue} overdue invoice${report.onStop.unlinkedOverdue === 1 ? " is" : "s are"} not linked to any customer, so ${report.onStop.unlinkedOverdue === 1 ? "it is" : "they are"} not counted here, and a customer with no linked Xero contact reads not checked until ${report.onStop.unlinkedOverdue === 1 ? "it is" : "they are"} linked.` : ""}`}{" "}
          <Link to="/office/debtors" className="underline">Show everyone</Link>
        </p>
      )}

      {syncMsg && <p role="status" className="mb-4 text-sm text-ele-green">{syncMsg}</p>}
      {chaserDone && <p role="status" className="mb-4 text-sm text-ele-green">{chaserDone}</p>}

      {!loading && !error && report && (
        <ListToolbar
          className="mb-4"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search customer or invoice number..."
          searchLabel="Search aged debtors"
        />
      )}

      {loading ? (
        <Loader rows={6} />
      ) : error || !report ? (
        <p role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          Could not load the aged debtor report. Please try again in a moment.
        </p>
      ) : (
        <>
          <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" hidden={report.onStop?.state === "not_checked"}>
            <div className="rounded-ele border border-ele-danger bg-ele-surface p-4">
              <div className="font-menu text-xs uppercase tracking-widest text-ele-blue">Total outstanding</div>
              <div className="mt-2 font-heading text-2xl text-ele-danger">{gbp(report.totalOutstanding)}</div>
              <div className="mt-1 text-xs text-ele-body opacity-60">
                {report.overdueCount} overdue invoice{report.overdueCount === 1 ? "" : "s"}
              </div>
            </div>
            {overdueGroups.map((g) => (
              <div key={g.band} className="rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
                <div className="font-menu text-xs uppercase tracking-widest text-ele-blue">{g.label}</div>
                <div className={`mt-2 font-heading text-2xl ${g.total > 0 ? "text-amber-400" : "text-ele-strong"}`}>{gbp(g.total)}</div>
                <div className="mt-1 text-xs text-ele-body opacity-60">{g.count} invoice{g.count === 1 ? "" : "s"}</div>
              </div>
            ))}
          </div>

          {report.onStop?.state === "not_checked" ? null : overdueGroups.every((g) => g.count === 0) ? (
            <EmptyState message={report.onStop ? "No customer is on stop." : "No overdue invoices. Everything outstanding is within terms."} />
          ) : (
            <>
              {overdueGroups
                .filter((g) => g.count > 0)
                .map((g) => <BandTable key={g.band} group={g} search={search} sort={sort} onChase={setChaserInvoice} />)}
              {search.trim() &&
                overdueGroups.filter((g) => g.count > 0).every((g) => filterRows(g.invoices, search, DEBTOR_SEARCH_ACCESSORS).length === 0) && (
                  <EmptyState role="status" message={`No overdue invoices match "${search.trim()}".`} />
                )}
            </>
          )}

          {current && current.count > 0 && (
            <div className="mt-6 opacity-80">
              <BandTable group={current} search={search} sort={sort} />
            </div>
          )}
        </>
      )}

      {chaserInvoice && (
        <DraftChaserModal
          invoiceId={chaserInvoice}
          onClose={() => setChaserInvoice(null)}
          onDone={() => {
            setChaserInvoice(null);
            setChaserDone("Chaser drafted and waiting in Gmail Drafts for a person to send.");
          }}
        />
      )}
    </div>
  );
}

function BandTable({
  group,
  search,
  sort,
  onChase,
}: {
  group: AgedBandGroup;
  search: string;
  sort: SortableColumnApi;
  onChase?: (invoiceId: string) => void;
}) {
  const navigate = useNavigate();
  // Card list below md, table otherwise -- decided once at mount (Session AHB).
  //
  // NOTE on the brief's card spec ("customer, total outstanding, 30/60/90 buckets as pills"):
  // that describes a per-CUSTOMER aggregate, and this report has no such shape. A band holds
  // INVOICES, and one customer can appear in several bands. The band heading above already is
  // the bucket, and the page's KPI row already shows every bucket's total prominently, so the
  // card carries the per-invoice facts the narrow table was mangling (amount 56px, due date
  // 56px at 375px) plus the days-overdue figure the table drops on mobile entirely.
  const [mobileCards] = useState(initialMobileLayout);
  // M-10, Session AQT: search narrows and sort orders each band's OWN rows -- one shared sort
  // state applied uniformly across every band's table, since they are the same columns over
  // different subsets. A band search excludes entirely renders nothing (the page-level check
  // above decides the "no matches" message so it is said once, not once per empty band).
  const rows = sortRows(filterRows(group.invoices, search, DEBTOR_SEARCH_ACCESSORS), DEBTOR_SORT_ACCESSORS, sort);
  if (rows.length === 0) return null;
  const heading = (
    <h2 className="mb-2 font-heading text-lg text-ele-blue">
      {group.label} <span className="text-sm text-ele-body">({rows.length} | {gbp(group.total)})</span>
    </h2>
  );

  if (mobileCards) {
    return (
      <div className="mb-6">
        {heading}
        <MobileCardList label={group.label}>
          {rows.map((inv) => (
            <MobileListCard
              key={inv.id}
              to={`/office/invoices/${encodeURIComponent(inv.id)}`}
              primary={inv.customerName}
              secondary={inv.invoiceNumber ?? "No invoice number"}
              meta={`Due ${formatDate(inv.dueDate)}`}
              right={
                <>
                  <span className="font-semibold text-ele-strong">{gbp(inv.amount)}</span>
                  {inv.daysOverdue > 0 && (
                    <StatusPill word={`${inv.daysOverdue} days overdue`} tone="amber" />
                  )}
                </>
              }
              actions={
                // The desktop row carries TWO routes out: the customer cell to the V2 invoice
                // and the invoice-number cell straight to Xero. The card keeps both, plus the
                // Phase 8 (Session ATJ, M-7) inline chase action on an overdue row.
                <div className="flex items-center gap-1">
                  {onChase && (
                    <button
                      type="button"
                      onClick={() => onChase(inv.id)}
                      aria-label={`Draft a chaser for ${inv.customerName}'s invoice`}
                      className="flex min-h-11 min-w-11 items-center justify-center rounded-ele border border-ele-purple-accent px-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
                    >
                      Chase
                    </button>
                  )}
                  <a
                    href={xeroHref(inv.xeroInvoiceId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`View ${inv.customerName}'s invoice in Xero`}
                    className="flex min-h-11 min-w-11 items-center justify-center rounded-ele border border-ele-purple-accent text-ele-body transition hover:border-ele-blue hover:text-ele-blue"
                  >
                    <ExternalLink aria-hidden className="h-4 w-4" />
                  </a>
                </div>
              }
            />
          ))}
        </MobileCardList>
      </div>
    );
  }

  return (
    <div className="mb-6">
      {heading}
      <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
        <table className="ele-list-table w-full text-left text-sm">
          <colgroup>
            <col style={{ width: "28%" }} />
            <col className="max-md:hidden" style={{ width: "16%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "14%" }} />
            <col className="max-md:hidden" style={{ width: "14%" }} />
            {onChase && <col className="max-md:hidden" style={{ width: "14%" }} />}
            {/* Mobile-only disclosure chevron: every row opens the invoice detail page. */}
            <col className="w-8 md:hidden" />
          </colgroup>
          <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
            <tr>
              <SortableHeader label="Customer" colKey="customer" sort={sort} onToggle={sort.toggle} />
              <SortableHeader label="Invoice" colKey="invoice" sort={sort} onToggle={sort.toggle} className="max-md:hidden" />
              <SortableHeader label="Amount" colKey="amount" sort={sort} onToggle={sort.toggle} align="right" />
              <SortableHeader label="Due date" colKey="due" sort={sort} onToggle={sort.toggle} />
              <SortableHeader label="Days overdue" colKey="overdue" sort={sort} onToggle={sort.toggle} align="right" className="cell-actions max-md:hidden" />
              {onChase && <th className="cell-actions max-md:hidden px-3 py-2" aria-hidden="true" />}
              <th className="w-8 cell-actions md:hidden" aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {rows.map((inv) => (
              <tr
                key={inv.id}
                className="group cursor-pointer border-t border-ele-purple-accent"
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest("a,button,input,select,textarea,label")) return;
                  navigate(`/office/invoices/${encodeURIComponent(inv.id)}`);
                }}
              >
                <td className="px-3 py-2 align-top text-ele-strong">
                  <Link to={`/office/invoices/${encodeURIComponent(inv.id)}`} className="text-ele-green underline-offset-2 hover:underline">
                    {inv.customerName}
                  </Link>
                </td>
                <td className="max-md:hidden px-3 py-2 align-top">
                  <a
                    href={xeroHref(inv.xeroInvoiceId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-ele-green hover:underline"
                  >
                    {inv.invoiceNumber ?? "View in Xero"}
                  </a>
                </td>
                <td className="px-3 py-2 text-right align-top text-ele-strong">{gbp(inv.amount)}</td>
                <td className="px-3 py-2 align-top text-ele-body">{formatDate(inv.dueDate)}</td>
                <td className={`cell-actions max-md:hidden px-3 py-2 text-right align-top ${inv.daysOverdue > 0 ? "text-amber-400" : "text-ele-body"}`}>
                  {inv.daysOverdue > 0 ? inv.daysOverdue : "-"}
                </td>
                {onChase && (
                  <td className="cell-actions max-md:hidden px-3 py-2 text-right align-top">
                    <button
                      type="button"
                      onClick={() => onChase(inv.id)}
                      className="rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
                    >
                      Chase
                    </button>
                  </td>
                )}
                <td className="cell-actions md:hidden pr-1 text-right">
                  <svg
                    aria-hidden
                    viewBox="0 0 16 16"
                    className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green group-focus-within:text-ele-green"
                  >
                    <path d="M6 4l4 4-4 4z" />
                  </svg>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
